import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

const t = {
  primary: '#7c3aed',
  primaryLight: '#ede9fe',
  surface: '#ffffff',
  border: '#e9e5f5',
  text: { primary: '#1e1b4b', secondary: '#6b7280', muted: '#9ca3b8' },
};

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  count?: number;
  dividerAfter?: boolean;
}

interface CustomSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: React.ReactNode;
  size?: 'sm' | 'md';
  className?: string;
}

export default function CustomSelect({
  options,
  value,
  onChange,
  placeholder = 'Sélectionner...',
  icon,
  size = 'md',
  className = '',
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label || placeholder;

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const isSm = size === 'sm';
  const triggerH = isSm ? 'h-8' : 'h-10';
  const textSize = isSm ? 'text-[11px]' : 'text-[12px]';

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 w-full ${triggerH} px-3 rounded-xl ${textSize} font-medium transition-all duration-200`}
        style={{
          backgroundColor: isOpen ? t.primaryLight : t.surface,
          border: `1px solid ${isOpen ? t.primary : t.border}`,
          color: selectedOption ? t.text.primary : t.text.muted,
          boxShadow: isOpen ? `0 0 0 3px ${t.primary}15` : 'none',
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {icon && (
          <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center" style={{ color: t.primary }}>
            {icon}
          </span>
        )}
        {selectedOption?.icon && (
          <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
            {selectedOption.icon}
          </span>
        )}
        <span className="flex-1 text-left truncate">{displayLabel}</span>
        {selectedOption?.count !== undefined && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
            style={{ backgroundColor: t.primaryLight, color: t.primary }}
          >
            {selectedOption.count}
          </span>
        )}
        <ChevronDown
          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: t.text.muted }}
        />
      </button>

      {/* Dropdown */}
      <div
        className="absolute z-50 left-0 right-0 mt-1.5 rounded-xl overflow-hidden"
        style={{
          backgroundColor: t.surface,
          border: `1px solid ${t.border}`,
          boxShadow: '0 10px 40px rgba(124, 58, 237, 0.12), 0 2px 8px rgba(0,0,0,0.06)',
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(-6px) scale(0.98)',
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'opacity 180ms ease, transform 180ms ease',
          maxHeight: '240px',
          overflowY: 'auto',
        }}
        role="listbox"
      >
        {options.map((option, i) => {
          const isSelected = option.value === value;
          return (
            <React.Fragment key={option.value}>
              <button
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`flex items-center gap-2.5 w-full px-3 ${isSm ? 'py-2' : 'py-2.5'} ${textSize} font-medium transition-all duration-150 text-left`}
                style={{
                  backgroundColor: isSelected ? t.primaryLight : 'transparent',
                  color: isSelected ? t.primary : t.text.primary,
                  border: 'none',
                  background: isSelected ? t.primaryLight : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = '#f9f8fc';
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                }}
                role="option"
                aria-selected={isSelected}
              >
                {option.icon && (
                  <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                    {option.icon}
                  </span>
                )}
                <span className="flex-1 truncate">{option.label}</span>
                {option.count !== undefined && (
                  <span className="text-[10px]" style={{ color: t.text.muted }}>
                    {option.count}
                  </span>
                )}
                {isSelected && (
                  <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: t.primary }} />
                )}
              </button>
              {option.dividerAfter && i < options.length - 1 && (
                <div className="mx-3 my-0.5" style={{ borderTop: `1px solid ${t.border}` }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
